import React, { useState, useEffect } from 'react';
import { Stepper } from'@shared/components/Stepper';
import { RequestDetailsStep } from'./stepComponents/RequestDetailsStep';
import { ReviewPaymentStep } from'./stepComponents/ReviewPaymentStep';
import { useNavigate, useSearchParams } from'react-router-dom';
import { ArrowLeft, MapPin } from'lucide-react';
import { PageHeader } from'@shared/components/PageHeader';
import { SelectedAccountsBanner } from '../../../../shared/components/SelectedAccountsBanner';
import { SelectAccountsStep } from '../../../../shared/components/SelectAccountsStep';
import { SimpleSuccessScreen } from '@shared/components/SimpleSuccessScreen';
import { getRemoteUtilityId } from '@shared/utils/getUtilityId';
import { getLoginDataFromStorage } from '@shared/utils/loginUtils';
import { ActiveAccountBanner } from '../../../../shared/components/ActiveAccountBanner';
import { useTranslation } from 'react-i18next';

export const CreateReconnect = () => {
 const { t } = useTranslation();
 const navigate = useNavigate();
 const [isSubmitted, setIsSubmitted] = useState(false);
 const [searchParams] = useSearchParams();
 
 const name = searchParams.get('name');
 const accountNo = searchParams.get('accountNo');
 const address = searchParams.get('address');
 const consumerInfo = name && accountNo && address ? { name, accountNo, address } : null;

 const showAccountSelection = searchParams.get('multiAccount') ==='true';
 
 useEffect(() => {
  return () => {
   localStorage.removeItem('reconnect-request-draft');
  };
 }, []);
 
 const baseSteps = [
 {
  title: t("Reconnect Details"),
  slug:"reconnect-details",
  component: <RequestDetailsStep remoteUtilityId={getRemoteUtilityId()} storageKey="reconnect-storage"/>,
  optional: false,
  infoTitle: t("Reconnect Details"),
  infoDescription: t("Fill in the reconnection details."),
 },
 {
  title: t("Review & Payment"),
  slug:"review-payment",
  component: <ReviewPaymentStep remoteUtilityId={1} storageKey="reconnect-storage"/>,
  optional: false,
  infoTitle: t("Review & Payment"),
  infoDescription: t("Review your request details and complete the payment."),
 },
 ];

 const steps = showAccountSelection 
 ? [
  {
   title: t("Select Accounts"),
   slug:"select-accounts",
   component: <SelectAccountsStep />,
   optional: false,
   infoTitle: t("Select Accounts"),
   infoDescription: t("Choose which sub-accounts this request applies to"),
  },
  ...baseSteps
  ]
 : baseSteps;

 return (
 <div className="p-6">
  <div className="mb-6">
   <PageHeader 
    title={t("Create Reconnection Request")} 
    subtitle={t("Follow the steps below to submit a new reconnection request")}
    icon={ArrowLeft} 
    onIconClick={() => navigate('/dashboard/requests')} 
   />
   {consumerInfo && (
    <div className="mt-4 bg-blue-50/50 border border-blue-100 rounded-xl p-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
     <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold">
       {consumerInfo.name.split(' ').map((n: string) => n[0]).join('').substring(0, 2)}
      </div>
      <div>
       <div className="font-bold text-gray-900 text-sm flex items-center gap-2">
        {consumerInfo.name} 
        <span className="text-gray-400 font-medium text-xs hidden sm:inline-block">({consumerInfo.accountNo})</span>
       </div>
       <div className="text-xs text-gray-500 flex items-center gap-1.5 mt-0.5">
        <MapPin className="w-3 h-3" />
        {consumerInfo.address}
       </div>
      </div>
     </div>
    </div>
   )}
   <ActiveAccountBanner />
  </div>
  
  {!isSubmitted ? (
   <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8 mt-2">
    <Stepper
    steps={steps}
    onStepChange={() => {}}
    onValidation={async () => ({ isValid: true })}
    onComplete={() => setIsSubmitted(true)}
    enableApiSave={false}
    saveOnStepChange={true}
    trackDirtyState={true}
    autoSave={true}
    persistProgress={true}
    storageKey="reconnect-request-draft"
    allowSkip={false}
    showDraftResume={true}
    />
   </div>
  ) : (
   <SimpleSuccessScreen
    title="Reconnection Request Submitted!"
    ticketId={`REC-${new Date().getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`}
    onPrimaryClick={() => navigate('/dashboard/requests')}
    onSecondaryClick={() => {
      setIsSubmitted(false);
      window.location.reload();
    }}
   />
  )}
 </div>
 );
};
